import {
  clearBrowserAccessToken,
  getBrowserAccessToken,
  getBrowserAuthHeaders,
  isCrossOriginUrl,
} from '@/lib/auth/browserAccessToken';
import { errorHandler } from '@/lib/errorHandler';
import { ResponseError } from '@/lib/exceptions';
import { logger } from '@/lib/logger';
import { genericApiErrorMessage } from '@/lib/routeHandler';
import { isUserNotSignedError } from '@/utils/checkUserNotSigned';

export const noHandlerForContentTypeError = (contentType: string | null | undefined) =>
  `No handler found for request with content-type ${contentType}.`;

export interface QueryParams {
  [key: string]: number | string | boolean | string[] | number[] | undefined;
}

type ContentType = 'application/json' | 'multipart/form-data' | 'application/octet-stream' | 'application/pdf';

interface RouteParams {
  baseUrl?: string;
  path: string;
  contentType?: ContentType;
  version?: number;
  params?: QueryParams;
  customHeaders?: { key: string; value: string }[] | null | undefined;
}

interface PostParams<T> extends RouteParams {
  baseUrl?: string;
  body?: T;
}

interface PostXMLParams<T> extends RouteParams {
  baseUrl?: string;
  body?: T;
  updateProgress?: (e: ProgressEvent) => void;
  onTimeoutError?: () => void;
  onError?: () => void;
  onLoad?: () => void;
  signal?: AbortSignal;
}

interface RequestParams<T> extends RouteParams {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  baseUrl?: string;
  body?: T;
  params?: QueryParams;
  customHeaders?: { key: string; value: string }[] | null | undefined;
}

interface XMLRequestParams<T> extends RequestParams<T> {
  updateProgress?: (e: ProgressEvent) => void;
  onTimeoutError?: () => void;
  onError?: () => void;
  onLoad?: () => void;
  signal?: AbortSignal;
}

class BaseService {
  public url: string;

  constructor(baseUrl: string = '/api/funding-service') {
    this.url = baseUrl;
  }

  /** Same-origin calls get their bearer from middleware; cross-origin calls must carry it themselves. */
  private resolveAuthHeaders = async (baseUrl: string): Promise<Record<string, string>> =>
    isCrossOriginUrl(baseUrl) ? getBrowserAuthHeaders() : {};

  /**
   * A cross-origin 401 can come from an API that rejects a still-valid host token, so only treat it
   * as session expiry when the host can no longer issue a token.
   */
  private isHostSessionExpired = async (baseUrl: string): Promise<boolean> => {
    if (!isCrossOriginUrl(baseUrl)) {
      return true;
    }
    clearBrowserAccessToken();
    return (await getBrowserAccessToken()) === null;
  };

  internalXMLHttpRequest = async <T, U>({
    baseUrl = this.url,
    method,
    path,
    body: input,
    version,
    contentType,
    customHeaders,
    onTimeoutError = () => {},
    onError = () => {},
    updateProgress = () => {},
    onLoad = () => {},
    signal,
  }: XMLRequestParams<T>): Promise<U> => {
    let headers: HeadersInit = {
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH',
      'Access-Control-Allow-Headers': 'Content-Type, Authorization',
      ...(await this.resolveAuthHeaders(baseUrl)),
    };

    let body: any = input;
    if (contentType && !contentType?.includes('multipart/form-data')) {
      headers['Content-type'] = contentType;
    }

    if (contentType?.includes('application/json')) {
      body = input ? JSON.stringify(input) : undefined;
    }

    if (version !== undefined) {
      headers['x-entity-version'] = version.toString();
    }
    if (customHeaders) {
      customHeaders.map((h: { key: string; value: string }) => (headers[h.key as keyof HeadersInit] = h.value));
    }

    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();

      request.open(method, `${baseUrl}/${path}`);

      Object.entries(headers).forEach(([key, value]) => {
        request.setRequestHeader(key, value);
      });

      request.upload.addEventListener('progress', updateProgress);
      request.upload.addEventListener('timeout', onTimeoutError);
      request.upload.addEventListener('error', onError);
      request.addEventListener('load', onLoad);

      request.addEventListener('load', () => {
        if (request.readyState === request.DONE) {
          if (request.status === 200) {
            resolve(JSON.parse(request.responseText));
          } else {
            if (request.status === 401) {
              void this.isHostSessionExpired(baseUrl).then(sessionExpired => {
                if (sessionExpired) {
                  console.warn('Session expired. Redirecting to login.');
                  window.location.href = '/auth/login';
                  return;
                }
                reject(new ResponseError('Unauthorized', 401, { cause: { status: 401, url: baseUrl } }));
              }, reject);
              return;
            }
            if (request.status === 403) {
              console.warn('Access denied. Redirecting to unauthorized page.');
              window.location.href = '/unauthorized';
              return;
            }
            reject(new Error('Error Request', { cause: { status: request.status } }));
          }
        }
      });

      request.addEventListener('loadend', function onLoadEnd() {
        request.upload.removeEventListener('progress', updateProgress);
        request.upload.removeEventListener('timeout', onTimeoutError);
        request.upload.removeEventListener('error', onError);
        request.removeEventListener('load', onLoad);
        request.removeEventListener('loadend', onLoadEnd);
      });

      request.addEventListener('error', () => {
        reject(new Error('Error Request', { cause: { status: request.status } }));
      });

      signal?.addEventListener('abort', () => {
        request.abort();
        reject(new Error('Request aborted'));
      });

      request.send(body);
    });
  };

  internalHttpRequest = async <T, U>({
    baseUrl = this.url,
    method,
    path,
    params,
    body: input,
    version,
    contentType,
    customHeaders,
  }: RequestParams<T>): Promise<U> => {
    let headers: HeadersInit = {
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH',
      'Access-Control-Allow-Headers': 'Content-Type, Authorization',
      ...(await this.resolveAuthHeaders(baseUrl)),
    };

    let body: any = input;
    if (contentType && !contentType?.includes('multipart/form-data')) {
      headers['Content-type'] = contentType;
    }

    if (contentType?.includes('application/json')) {
      body = input ? JSON.stringify(input) : undefined;
    }

    if (version !== undefined) {
      headers['x-entity-version'] = version.toString();
    }
    if (customHeaders) {
      customHeaders.map((h: { key: string; value: string }) => (headers[h.key as keyof HeadersInit] = h.value));
    }
    const cleanParams = this.cleanQueryParams(params);

    try {
      const data = await fetch(`${baseUrl}/${path}${cleanParams}`, {
        method,
        body,
        headers,
        credentials: 'include', // Ensure cookies (including okta-access-token) are sent with requests
      }).then(async res => {
        const responseContentType = res.headers.get('content-type');

        // Check error responses FIRST — independent of request content-type.
        // Otherwise multipart uploads bypass error handling and return the
        // raw error Response to the caller, which then `res.json()`s the
        // error envelope and treats it as a success payload.
        if (!res?.ok) {
          // Only try to parse JSON if the response has JSON content-type and a body
          let responseData: any = {};
          try {
            if (
              responseContentType &&
              (responseContentType.includes('application/json') ||
                responseContentType.includes('application/problem+json'))
            ) {
              responseData = await res.json();
            }
          } catch (e) {
            // Empty response or invalid JSON, ignore
          }

          if (res.status === 401 && (await this.isHostSessionExpired(baseUrl))) {
            console.warn('Session expired. Redirecting to login.');
            window.location.href = '/auth/login';
            return;
          }
          if (res.status === 403) {
            console.warn('Access denied. Redirecting to unauthorized page.');
            window.location.href = '/unauthorized';
            return;
          }
          if (
            responseContentType &&
            (responseContentType.includes('application/json') ||
              responseContentType.includes('application/problem+json'))
          ) {
            if (res.status === 401 && responseData.message === 'User not signed in') {
              logger({ service: 'client', level: 'INFO', body: { error: responseData.message } });
              return false;
            }

            // Collateral Service message error is generic
            if (res.status === 422) {
              throw new ResponseError(genericApiErrorMessage, 422);
            }

            const errorMessage =
              responseData?.message || responseData?.detail || responseData?.title || 'Something went wrong';

            throw new ResponseError(errorMessage, res.status, {
              cause: { ...responseData, status: res.status },
            });
          } else {
            throw new Error(genericApiErrorMessage);
          }
        }

        // Successful response: callers expecting a raw Response (204, non-JSON
        // request like multipart upload, or non-JSON response) get the Response.
        if (
          res.status === 204 ||
          !contentType ||
          (contentType && !contentType.includes('application/json')) ||
          (responseContentType && !responseContentType.includes('application/json'))
        ) {
          return res;
        }

        // Parse JSON response for successful requests
        const responseData = await res.json();
        return responseData;
      });
      return data;
    } catch (e: any) {
      const responseError = errorHandler(e);
      if (e instanceof ResponseError && isUserNotSignedError(e)) {
        logger({ service: 'client', level: 'INFO', body: { url: baseUrl, error: e.message } });
      } else if (!isUserNotSignedError(e)) {
        logger({ service: 'client', level: 'ERROR', body: { url: baseUrl, error: e } });
      }

      throw responseError;
    }
  };

  get = async <U>({
    baseUrl,
    path,
    params,
    version,
    contentType = 'application/json',
    customHeaders,
  }: RouteParams): Promise<U> => {
    return this.internalHttpRequest({ baseUrl, method: 'GET', path, params, version, contentType, customHeaders });
  };

  postXML = async <T, U>({
    baseUrl,
    path,
    params,
    body = undefined as T,
    version,
    contentType = 'application/json',
    customHeaders,
    onTimeoutError,
    onError,
    updateProgress,
    onLoad,
    signal,
  }: PostXMLParams<T>): Promise<U> => {
    return this.internalXMLHttpRequest({
      method: 'POST',
      baseUrl,
      path,
      params,
      body,
      version,
      contentType,
      customHeaders,
      onTimeoutError,
      onError,
      updateProgress,
      onLoad,
      signal,
    });
  };

  post = async <T, U>({
    baseUrl,
    path,
    params,
    body = undefined as T,
    version,
    contentType = 'application/json',
    customHeaders,
  }: PostParams<T>): Promise<U> => {
    return this.internalHttpRequest({
      method: 'POST',
      baseUrl,
      path,
      params,
      body,
      version,
      contentType,
      customHeaders,
    });
  };

  put = async <T, U>({
    baseUrl,
    path,
    params,
    body = undefined as T,
    version,
    contentType = 'application/json',
    customHeaders,
  }: PostParams<T>): Promise<U> => {
    return this.internalHttpRequest({
      baseUrl,
      method: 'PUT',
      path,
      params,
      body,
      version,
      contentType,
      customHeaders,
    });
  };

  patch = async <T, U>({
    baseUrl,
    path,
    params,
    body = undefined as T,
    version,
    contentType = 'application/json',
    customHeaders,
  }: PostParams<T>): Promise<U> => {
    return this.internalHttpRequest({
      baseUrl,
      method: 'PATCH',
      path,
      params,
      body,
      version,
      contentType,
      customHeaders,
    });
  };

  delete = async <U>({
    baseUrl,
    path,
    params,
    version,
    contentType = 'application/json',
    customHeaders,
  }: RouteParams): Promise<U> => {
    return this.internalHttpRequest({ baseUrl, method: 'DELETE', path, params, version, contentType, customHeaders });
  };

  cleanQueryParams = (params?: QueryParams): string => {
    if (!params) {
      return '';
    }
    // Remove undefined values
    Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
    const paramString = this.createQueryString(params);

    return paramString;
  };

  // We use this instead of URLSearchParams because we need to keep seperate entries for duplicate keys with different values e.g. ?name=1&name=2
  createQueryString = (params?: QueryParams): string => {
    let parts: string[] = [];
    for (const key in params) {
      // Ensure the property belongs to the object itself, not its prototype chain
      if (params.hasOwnProperty(key)) {
        const values = params[key];

        if (Array.isArray(values)) {
          values?.forEach(value => {
            // Encode the key and value to ensure they are properly escaped
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
          });
        } else if (values !== undefined) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(values)}`);
        }
      }
    }
    return parts.length ? '?' + parts.join('&') : '';
  };
}

export default BaseService;
